import { isEqual } from "lodash";

import { PanelState, State, Layout } from "@/shared/state/visualization";

export interface VizStateChangeSet {
  panels: {
    /**
     * Panels that exist in the initial state but not in the current state
     */
    added: Record<string, PanelState>;

    /**
     * Panels that exist in the current state but not in the initial state
     */
    removed: Record<string, PanelState>;

    /**
     * Panels that exist in both states but have changed
     */
    changed: Record<string, PanelState>;
  };
  layout: {
    changed: Layout | null;
  };
  hasChanges: boolean;
}

/**
 * Computes the difference between two VizState's panels and layouts
 */
export function diffVizState(
  initialState: Pick<State, "panels" | "layout">,
  currentState: Pick<State, "panels" | "layout">,
): VizStateChangeSet {
  const initialPanels = initialState.panels ?? {};
  const currentPanels = currentState.panels ?? {};

  const removed: Record<string, PanelState> = {};
  const added: Record<string, PanelState> = {};
  const changed: Record<string, PanelState> = {};

  // Find removed panels (in initial but not in current)
  Object.entries(initialPanels).forEach(([id, panel]) => {
    if (!currentPanels[id]) {
      removed[id] = panel;
    }
  });

  // Find added and changed panels
  Object.entries(currentPanels).forEach(([id, currentPanel]) => {
    const initialPanel = initialPanels[id];

    if (!initialPanel) {
      // Panel exists in current but not in initial
      added[id] = currentPanel;
    } else {
      // Panel exists in both, check if it changed
      // We use deep comparison to detect changes in nested properties
      if (!isEqual(initialPanel, currentPanel)) {
        changed[id] = currentPanel;
      }
    }
  });

  const initialLayout = initialState.layout;
  const currentLayout = currentState.layout;

  const hasLayoutChanged = !isEqual(initialLayout, currentLayout);

  const hasChanges =
    hasLayoutChanged ||
    Object.keys(removed).length > 0 ||
    Object.keys(added).length > 0 ||
    Object.keys(changed).length > 0;

  return {
    panels: {
      removed,
      added,
      changed,
    },
    layout: {
      changed: hasLayoutChanged ? currentLayout : null,
    },
    hasChanges,
  };
}
